import { AfterViewInit, ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';

import {
  TreeFormControl,
  TreeFormControlFlatNode,
  TreeFormControlNode,
  TreeFormControlOptions
} from './tree-form-control';

/**
 * Tree Editor form field component
 *
 * This component provides UI to create a hierarchy (tree)
 * which is later rendered by the `TreeSelectorFormControlComponent` component.
 */
@Component({
    selector: 'eas-tree-editor-form-control',
    templateUrl: './tree-editor-form-control.component.html',
    styleUrls: ['./tree-editor-form-control.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TreeEditorFormControlComponent),
            multi: true
        }],
    standalone: false
})
export class TreeEditorFormControlComponent implements AfterViewInit, ControlValueAccessor {
  /**
   * Options for the component
   *
   * @note Not used at the moment.
   */
  @Input() options: TreeFormControlOptions = {
    allowsHierarchy: true
  }

  //
  // Material Tree infrastructure, see Material Tree documentation
  //
  public dataSource: MatTreeFlatDataSource<TreeFormControlNode, TreeFormControlFlatNode>;
  public treeControl: FlatTreeControl<TreeFormControlFlatNode>;
  public treeFlattener: MatTreeFlattener<TreeFormControlNode, TreeFormControlFlatNode>;

  /**
   * Angular form control value, this is an array of TreeFormControlNode objects
   * describing the hierarchy.
   */
  public value: TreeFormControlNode[] = [];

  constructor(private cd: ChangeDetectorRef) {
    this.treeControl = new FlatTreeControl<TreeFormControlFlatNode>(
      node => node.level,
      node => node.expandable,
    );

    this.treeFlattener = new MatTreeFlattener<TreeFormControlNode, TreeFormControlFlatNode>(
      TreeFormControl.nodeToFlatNode,
      node => node.level,
      node => node.expandable,
      node => node.categories,
    );

    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  }

  ngAfterViewInit() {}

  //
  // ControlValueAccessor interface
  //

  private onChange = (_: any) => {};
  //private onTouch = () => {};

  writeValue(value: TreeFormControlNode[]) {
    if (typeof value === 'string') { this.value = []; }
    else { this.value = value ?? []; }

    this.onChange(this.value);
    this.dataSource.data = this.value;
    this.treeControl.expandAll();
    this.cd.detectChanges();
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(_fn: any) {
    //this.onTouch = fn;
  }

  //
  // Tree helper methods
  //

  public hasChild(_: number, node: TreeFormControlFlatNode): Boolean {
    return node.expandable;
  }

  public isEmpty(): Boolean {
    return this.dataSource.data.length === 0;
  }

  onAddRoot() {
    if (this.dataSource.data.length > 0) { return; }

    const node = TreeFormControl.emptyNode();
    this.writeValue([node]);
  }

  onAddSibling(node: TreeFormControlFlatNode) {
    const newNode = TreeFormControl.emptyNode();
    if (node.level === 0) {
      this.value.push(newNode);
    } else {
      const parentNode = TreeFormControl.findParentNodeForChildNodeWithID(node.id, undefined, this.value);
      parentNode?.categories.push(newNode);
    }
    this.writeValue(this.value);
  }

  onAddChild(node: TreeFormControlFlatNode) {
    if (!this.options.allowsHierarchy) { return; }

    const newNode = TreeFormControl.emptyNode();
    const currentNode = TreeFormControl.findNodeByID(node.id, this.value);
    currentNode?.categories.push(newNode);

    this.writeValue(this.value);
  }

  onDelete(node: TreeFormControlFlatNode) {
    if (node.level === 0) {
      this.value = this.value.filter(n => n._id !== node.id);
    } else {
      const parentNode = TreeFormControl.findParentNodeForChildNodeWithID(node.id, undefined, this.value);
      if (!parentNode) { return; }

      const index = parentNode.categories.findIndex(n => n._id === node.id);
      parentNode.categories.splice(index,1);
    }
    this.writeValue(this.value);
  }

  //
  // Angular element events
  //

  onInputUpdate(value: string, node: TreeFormControlFlatNode) {
    const nodeToUpdate = TreeFormControl.findNodeByID(node.id, this.value);
    if (!nodeToUpdate) { return; }

    nodeToUpdate.name = value;
    this.writeValue(this.value);
  }
}
